import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { caseStudies } from '../content/editorial';
import { WORK_VISUALS } from '../content/workVisuals';
import {
  EditorialContainer,
  EditorialArchivePageShell,
  EditorialArchiveSection,
  Eyebrow,
  PageDeck,
  PageTitle,
  TopSpacer,
  usePublicationMeta
} from '../components/editorial/PublicationShared';

const IntroWrap = styled.div`
  max-width: 48rem;
`;

const HeroSection = styled.section`
  padding: 0 0 3.25rem;
  background: transparent;
  color: #1f2937;
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroEyebrow = styled(Eyebrow)`
  color: #8c5b24;
`;

const HeroTitle = styled(PageTitle)`
  max-width: none;
  color: #1f2937;
  text-shadow: 0 10px 30px rgba(255, 255, 255, 0.22);
`;

const HeroDeck = styled(PageDeck)`
  max-width: 44rem;
  color: #334155;
`;

const WorksGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 320px));
  // justify-content: center;
  gap: 1.35rem;
  position: relative;
  z-index: 1;
`;

const WorkCardLink = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  perspective: 1400px;
`;

const WorkCard = styled.article<{ $glow: string }>`
  position: relative;
  display: grid;
  grid-template-rows: minmax(190px, 220px) auto;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(16, 22, 37, 0.08);
  background: rgba(255, 255, 255, 0.76);
  box-shadow:
    0 18px 36px rgba(16, 22, 37, 0.12),
    0 10px 24px ${({ $glow }) => $glow};
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    border-color 180ms ease;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(224, 161, 94, 0.35);
    box-shadow:
      0 24px 48px rgba(16, 22, 37, 0.16),
      0 16px 34px ${({ $glow }) => $glow};
  }
`;

const WorkVisual = styled.div<{
  $background: string;
  $imageFit: 'cover' | 'contain';
  $imagePosition: string;
  $imageScale: number;
}>`
  position: relative;
  background: ${({ $background }) => $background};
  padding: 1rem 1rem 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkImage = styled.img<{
  $imageFit: 'cover' | 'contain';
  $imagePosition: string;
  $imageScale: number;
}>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $imageFit }) => $imageFit};
  object-position: ${({ $imagePosition }) => $imagePosition};
  transform: scale(${({ $imageScale }) => $imageScale});
  display: block;
  filter: drop-shadow(0 16px 26px rgba(10, 17, 28, 0.1));
`;

const WorkBody = styled.div`
  padding: 1rem 1rem 1.15rem;
  display: grid;
  gap: 0.7rem;
  background: rgba(255, 255, 255, 0.7);
`;

const CategoryPill = styled.span`
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.38rem 0.72rem;
  border-radius: 999px;
  border: 1px solid rgba(224, 161, 94, 0.42);
  background: rgba(255, 249, 241, 0.9);
  color: #8c5b24;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const WorkTitle = styled.h2`
  margin: 0;
  color: #101625;
  font-size: clamp(1.4rem, 2vw, 1.9rem);
  line-height: 0.98;
  letter-spacing: -0.05em;
  font-family: 'Fraunces', Georgia, serif;
  text-wrap: balance;
`;

export default function WorksPage() {
  usePublicationMeta(
    'Works',
    'Selected CyBard case studies covering brand, growth, digital systems, and custom delivery work.'
  );

  return (
    <EditorialArchivePageShell>
      <HeroSection>
        <TopSpacer />
        <EditorialContainer>
          <HeroInner>
            <IntroWrap>
              <HeroEyebrow>CyBard Works</HeroEyebrow>
              <HeroTitle>Case Studies</HeroTitle>
              <HeroDeck>
                Field notes from client work where strategy, design, and technical systems had to
                operate as one coherent delivery model.
              </HeroDeck>
            </IntroWrap>
          </HeroInner>
        </EditorialContainer>
      </HeroSection>

      <EditorialArchiveSection>
        <EditorialContainer>
          <WorksGrid>
            {caseStudies.map((study) => {
              const visual = WORK_VISUALS[study.slug] ?? {
                image: '/images/CybardHeroMaybe.png',
                background:
                  'radial-gradient(circle at top right, rgba(224, 161, 94, 0.14), transparent 42%), linear-gradient(135deg, rgba(245, 241, 235, 0.96), rgba(236, 232, 226, 0.94))',
                imageFit: 'contain' as const,
                imagePosition: 'center center',
                imageScale: 1
              };

              return (
                <WorkCardLink key={study.slug} to={`/works/${study.slug}`}>
                  <WorkCard $glow={study.theme.glow}>
                    <WorkVisual
                      $background={visual.background}
                      $imageFit={visual.imageFit ?? 'cover'}
                      $imagePosition={visual.imagePosition ?? 'center center'}
                      $imageScale={visual.imageScale ?? 1}
                    >
                      <WorkImage
                        src={visual.image}
                        alt={`${study.client} case study preview`}
                        $imageFit={visual.imageFit ?? 'cover'}
                        $imagePosition={visual.imagePosition ?? 'center center'}
                        $imageScale={visual.imageScale ?? 1}
                      />
                    </WorkVisual>
                    <WorkBody>
                      <CategoryPill>{study.category}</CategoryPill>
                      <WorkTitle>{study.client}</WorkTitle>
                    </WorkBody>
                  </WorkCard>
                </WorkCardLink>
              );
            })}
          </WorksGrid>
        </EditorialContainer>
      </EditorialArchiveSection>
    </EditorialArchivePageShell>
  );
}
